import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { Session } from './types/session.type';
import { LOCALSTORAGE_KEYS, FIREBASE_COLLECTIONS } from './constants/databases';
import { OrderService } from './order.service';
import { FirebaseCrudService } from './firebase-crud.service';
import { CategorySelected, Frequencies, MarketStatus, UserPreferences } from './types/common.types';
import { Subscription } from '../settings/account/subscriptions/subscriptions.types';
import { SidebarCheckList, SidebarFilterTypes } from './sidebar/sidebar.types';
import { ModalContentService } from './modal-content/modal-content.service';
import { ModalContentTypes } from './constants/modal-content-types';
import { DateTime } from 'luxon';
import { environment } from '../../environments/environment';
import { eCommercePermissions } from './types/account.types';
import { FilterTypes } from './header/search-bar/search-bar.types';
import { formatDateToReadableString } from './utils/formatting';

@Injectable({
  providedIn: 'root'
})
export class SignalsStoreService {
  private firebaseCrudService = inject(FirebaseCrudService);
  private localStorageService = inject(LocalStorageService);
  private orderService = inject(OrderService);
  private modalContentService = inject(ModalContentService);

  anonymousUserAddress: WritableSignal<any> = signal(null);
  donationAmountSkippable: WritableSignal<number> = signal(0);
  donationVoluntary: WritableSignal<any | null> = signal(null);
  tip: WritableSignal<any | null> = signal(null);
  odooDonationAmountVoluntary: WritableSignal<number | null> = signal(null);
  odooTipAmount: WritableSignal<number | null> = signal(null);
  totalOrderAmount: WritableSignal<number> = signal(0);
  googleAddress: WritableSignal<any> = signal(null)
  hasSession: WritableSignal<any> = signal(false)
  hasSkippedWeekDelivery: WritableSignal<boolean> = signal(false);
  isContentLoaded: WritableSignal<any> = signal(false);
  isSessionLoaded: WritableSignal<any> = signal(false);
  isSidebarOpen: WritableSignal<any> = signal(true);
  sessionSignal: WritableSignal<Session | null> = signal(null);
  signUpStep: WritableSignal<any> = signal(0)
  selectedTerritory = signal<any>(null);
  selectedCategory: WritableSignal<{ id: number, name: string }> = signal({ id: -1, name: '' });

  frequencies: WritableSignal<Frequencies[]> = signal([]);
  subscriptions: WritableSignal<Subscription[]> = signal([]);
  marketStatus: WritableSignal<MarketStatus> = signal({ isOpen: true, openingDay: '', openingHour: '' });
  filterByProducer: WritableSignal<any> = signal(null);

  showCartPreview: WritableSignal<boolean> = signal(false);

  isFiltering: WritableSignal<boolean> = signal(false);

  justAddedProduct: WritableSignal<any> = signal(null);

  shouldShowCategoriesBar: WritableSignal<boolean> = signal(true);

  isLimitedUser: WritableSignal<boolean> = signal(true);

  isAddressRequired: WritableSignal<boolean> = signal(true);

  permissions: WritableSignal<eCommercePermissions | null> = signal(null);

  isCustomBoxSignupFlow: WritableSignal<boolean> = signal(false);

  isShowingMobileFooter: WritableSignal<boolean> = signal(true);

  // This variable is set preventing for logo.svg caching in some browsers:
  // TODO: There is a better solution?
  logoVersionNumber: WritableSignal<number> = signal(0);

  // Products to remove from carousels for order and cart preview components:
  hiddenCarouselProducts: WritableSignal<number[]> = signal([]);

  menuSignal: WritableSignal<any[]> = signal([]);
  menu: any[] = [];

  isShopBannerHidden = signal(false);

  isInOrderComponent = signal(false);

  #signupPromotionInfo: WritableSignal<any> = signal(null);
  signupPromotionInfo: Signal<any> = computed(() => this.#signupPromotionInfo());

  #signupPromotionInfoLS: WritableSignal<any> = signal(null);
  signupPromotionInfoLS: Signal<any> = computed(() => this.#signupPromotionInfoLS());

  #isLoadedMarketingValidation: WritableSignal<boolean> = signal(false);
  isLoadedMarketingValidation: Signal<boolean> = computed(() => this.#isLoadedMarketingValidation());

  userPreferences = signal<UserPreferences>({
    dontShowAgainPrevDislike: {
      value: false,
      date: 0
    },
    holdUpSubscriptions: {
      value: false,
      date: 0
    }
  });

  categoryInViewPort = signal<CategorySelected | null>(null);

  globalMessages = signal<any[]>([]);

  showCartPreviewInBLogPost = signal(false);

  isInBlogPost = signal(false);

  #isInShopComponent = signal(false);
  getIsInShopComponent = computed(() => this.#isInShopComponent());
  setIsInShopComponent = (value: boolean) => this.#isInShopComponent.set(value);

  #filters: WritableSignal<Map<string, { type: FilterTypes; value: string }>> = signal(new Map());
  getFilters = computed(() => this.#filters());
  setFilters = (value: Map<string, { type: FilterTypes; value: string }>) => this.#filters.update(() => value);

  #filterChipRemoved: WritableSignal<string> = signal('');
  getFilterChipRemoved = computed(() => this.#filterChipRemoved());
  setFilterChipRemoved = (value: string) => this.#filterChipRemoved.update(() => value);

  globalFilter = signal<{ type: string, map: Map<string, number[]> }>({ type: '', map: new Map() });

  isLoadedDeliveryInfo = signal(false);

  isSubscriptionUpdateOrigin = signal(false);

  constructor() {
    this.sessionSignal.set(this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) || null)
    const hasSession = !!(this.sessionSignal())?.accountInfo?.id;
    this.hasSession.set(hasSession);
    this.isLimitedUser.set(!!(this.sessionSignal()?.settings.isLimited));

    // Require address:
    if (this.sessionSignal()?.settings.requireAddress === false)
      this.isAddressRequired.set(false)
    else
      this.isAddressRequired.set(true);


    this.isSessionLoaded.set(true);
    this.setUpAnonymousAddress();

    this.logoVersionNumber.set(environment.config.filesVersionNumber.logo);
  }

  private setUpAnonymousAddress() {
    const storedAnonymousAddress: any = this.localStorageService.get(LOCALSTORAGE_KEYS.ANONYMOUS_ADDRESS);
    if (!!(storedAnonymousAddress?.street)) {
      this.anonymousUserAddress.set(storedAnonymousAddress);
    }
  }

  getAndSyncExpiredOrder(mock?: ExpiredOrderResponse) {
    if (mock) {
      this.validateExpiredFirebaseOrder(mock);
      return;
    }

    const sessionStored: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) || null;
    if (!sessionStored?.accountInfo?.id) return

    this.firebaseCrudService.getById(FIREBASE_COLLECTIONS.EXPIRED_ORDERS, sessionStored.accountInfo.id.toString(), (res: ExpiredOrderResponse) => {
      if (!res) return;

      if (this.validateExpiredFirebaseOrder(res)) {
        this.clearFBExpiredOrder();
        return;
      }
    })
  }

  triggerExpiredOrderMock() {
    const mock = this.buildExpiredOrderMock();
    this.getAndSyncExpiredOrder(mock);
  }

  private buildExpiredOrderMock(): ExpiredOrderResponse {
    const now = DateTime.now();
    const delivery = now.plus({ days: 7 }).toISODate();
    const cutoff = now.minus({ hours: 1 }).toISODate();

    return {
      deliveryDate: delivery,
      cutoffDate: cutoff,
      products: [
        {
          name: 'Organic Apples',
          priceUnit: '3.5',
          isSubscription: false,
          qtyConfirmed: 1,
          qtyUnconfirmed: 3,
          packageName: '',
          qtyPackageConfirmed: 0,
          qtyPackageUnconfirmed: 0
        },
        {
          name: 'Weekly Veggie Box',
          priceUnit: '25',
          isSubscription: true,
          qtyConfirmed: 0,
          qtyUnconfirmed: 4,
          packageName: 'Medium',
          qtyPackageConfirmed: 0,
          qtyPackageUnconfirmed: 1
        },
        {
          name: 'Free-range Eggs (Dozen)',
          priceUnit: '$6.99',
          isSubscription: false,
          qtyConfirmed: 0,
          qtyUnconfirmed: 2,
          packageName: '',
          qtyPackageConfirmed: 0,
          qtyPackageUnconfirmed: 0
        },
        {
          name: 'Custom Fruit Box',
          priceUnit: '40',
          isSubscription: false,
          qtyConfirmed: 0,
          qtyUnconfirmed: 0,
          packageName: 'Large',
          qtyPackageConfirmed: 0,
          qtyPackageUnconfirmed: 2
        },
        {
          name: 'Single-Origin Coffee Beans',
          priceUnit: '12.5',
          isSubscription: false,
          qtyConfirmed: 0,
          qtyUnconfirmed: 1,
          packageName: '',
          qtyPackageConfirmed: 0,
          qtyPackageUnconfirmed: 0
        }
      ],
      coupons: [
        { code: 'WELCOME10', discount: '10% off your order' },
        { code: 'FREESHIP', discount: 'Free shipping over $50' },
        { code: 'SUB5', discount: 'Save $5 on subscriptions' }
      ]
    };
  }

  getAndSyncSubscriptionTrigger(callback: (data: OrderAndSubsUpdateResponse | null) => void) {
    const sessionStored: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) || null;
    if (!sessionStored?.accountInfo?.id) return callback(null);
    return this.firebaseCrudService.getById(FIREBASE_COLLECTIONS.ORDER_AND_SUBS_UPDATES, sessionStored.accountInfo.id.toString(), (res: OrderAndSubsUpdateResponse) => callback(res));
  }

  private validateExpiredFirebaseOrder(res: ExpiredOrderResponse): boolean {
    if (res?.deliveryDate && res?.products?.length && res?.cutoffDate) {

      const cutoffDate = formatDateToReadableString(res.cutoffDate).mmddyyyyFormat || '';
      const deliveryDate = formatDateToReadableString(res.deliveryDate).mmddyyyyFormat || '';
      const commonProductsLostList = this.setUpProductsLostList(res.products.filter(e => !e.isSubscription) || [], 'A la carte');
      const subscriptionProductsLostList = this.setUpProductsLostList(res.products.filter(e => e.isSubscription) || [], 'Subscription');

      const couponsLostList = this.setUpCouponsLostList(res.coupons || []);

      const productsLostContainer = commonProductsLostList.length || subscriptionProductsLostList.length ? `
        <ul class="list-group list-group-flush">
          ${commonProductsLostList.join('')}
          ${subscriptionProductsLostList.join('')}
        </ul>` : '';

      const couponsLostContainer = couponsLostList.length ? `
        <ul class="list-group list-group-flush">
          ${couponsLostList.join('')}
        </ul>` : '';

      this.modalContentService.openModal(ModalContentTypes.CONFIRMATION, {
        title: 'Order Expired: Unsaved Changes Lost',
        textContent: `
        <span>
          Your order for delivery on <strong>${deliveryDate}</strong> has expired because the cutoff date has passed (<strong>${cutoffDate}</strong>). Unfortunately, some changes you made were not confirmed — this may include products you added but didn’t save, or quantity changes that weren’t finalized. The following are the changes that were not applied to your delivery order:
        </span>
        <div class="col-md-12 mt-4">
          ${productsLostContainer}
        </div>
        <hr>
        <div class="col-md-12 mt-4">
          ${couponsLostContainer}
        </div>
        `,
        confirmButtonText: 'I understand',
        closeable: false
      }, { backdrop: 'static', keyboard: false });

      return true;
    }

    return false;
  }

  private setUpProductsLostList(list: ExpiredOrderProducts[], type: string): string[] {
    return (list || []).map((p) => {
      const formattedPrice = (() => {
        const raw = (p.priceUnit ?? '').toString().trim();
        const num = Number(raw.replace(/[^0-9.-]/g, ''));
        if (!Number.isNaN(num) && raw !== '') return `$${num.toFixed(2)}`;
        return raw || '-';
      })();

      const hasPackage = !!(p.packageName && p.packageName.toString().trim() !== '');
      const intended = hasPackage ? (p.qtyPackageUnconfirmed ?? 0) : (p.qtyUnconfirmed ?? 0);
      const saved = hasPackage ? (p.qtyPackageConfirmed ?? 0) : (p.qtyConfirmed ?? 0);
      const qtyLabel = hasPackage ? 'Package qty' : 'Intended qty';

      const nameHtml = hasPackage
        ? `<span class="fw-semibold">${p.name}</span><span class="badge bg-secondary-subtle text-secondary-emphasis ms-2">${p.packageName}</span>`
        : `<span class="fw-semibold">${p.name}</span>`;

      const isZeroSaved = Number(saved) === 0;
      const deliveryHint = isZeroSaved
        ? `<div class="small text-warning d-flex align-items-center gap-2 mt-1">
             <span class="badge bg-warning-subtle text-warning-emphasis">0 saved</span>
             <span>None will be delivered</span>
           </div>`
        : `<div class="small text-success d-flex align-items-center gap-2 mt-1">
             <span class="badge bg-success-subtle text-success-emphasis">Saved ${saved}</span>
             <span>${saved} will be delivered</span>
           </div>`;

      return `
        <li class="list-group-item py-3">
          <div class="d-flex justify-content-between align-items-start text-start">
            <div class="me-3">
              <div class="d-flex align-items-center flex-wrap gap-2">${nameHtml}</div>
              <div class="small text-muted">Unit price: ${formattedPrice}</div>
              <div class="small text-muted">${qtyLabel}: ${intended} | Saved: ${saved}</div>
              ${deliveryHint}
            </div>
            <span class="badge text-bg-primary rounded-pill align-self-start">${type}</span>
          </div>
        </li>
        `;
    });
  }

  private setUpCouponsLostList(list: ExpiredOrderCoupons[]): string[] {
    return (list || []).map((c) => {
      return `
        <li class="list-group-item py-3">
          <div class="d-flex justify-content-between align-items-start text-start">
            <div class="me-3">
              <div class="fw-semibold">${c.code}</div>
              <div class="small text-muted">${c.discount}</div>
            </div>
            <span class="badge text-bg-primary rounded-pill align-self-start">Coupon</span>
          </div>
        </li>
        `;
    });
  }

  private clearFBExpiredOrder(): void | null {
    const sessionStored: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) || null;
    if (!sessionStored?.accountInfo?.id) return

    this.firebaseCrudService.delete(FIREBASE_COLLECTIONS.EXPIRED_ORDERS, sessionStored.accountInfo.id.toString());
  }

  setSignupPromotionInfo(value: any) {
    this.#signupPromotionInfo.update(() => value);
    if (value)
      this.#signupPromotionInfoLS.update(() => value);
  }

  setIsLoadedMarketingValidation(value: any) {
    this.#isLoadedMarketingValidation.update(() => value);
  }
}

export interface FirebaseResponse {
  cutoffDate?: number;
  expired?: boolean;
  eventCreated?: boolean;
  cartProducts?: any[];
  orderProducts?: FirebaseOrder;
}

export interface ExpiredOrderResponse {
  deliveryDate: string;
  cutoffDate: string;
  name?: boolean;
  products?: ExpiredOrderProducts[] | null;
  coupons?: ExpiredOrderCoupons[] | null;
}

export interface ExpiredOrderProducts {
  name: string;
  priceUnit: string;
  isSubscription: boolean;
  qtyConfirmed: number;
  qtyUnconfirmed: number;
  packageName: string;
  qtyPackageConfirmed: number;
  qtyPackageUnconfirmed: number;
}

export interface ExpiredOrderCoupons {
  code: string;
  discount: string;
}

export interface FirebaseOrder {
  products: {
    common: any[];
    subscription: any[];
  },
  paymentDetails?: {
    tip: {
      amount?: number;
      isRecurrent?: boolean;
      isDeleted?: boolean;
    };
    donation: {
      amount?: number;
      isRecurrent?: boolean;
      isDeleted?: boolean;
    };
  }
};

export interface OrderAndSubsUpdateResponse {
  shouldRefresh: boolean;
  date: number;
}